import React, { useEffect, useState, useCallback } from "react";
import Calendar from "react-calendar";
import 'react-calendar/dist/Calendar.css';
import { useSwipeable } from "react-swipeable";
import { ref, get } from "firebase/database"; 
import { rtdb } from "../firebase";
import FgDetailModal from "./FgDetailModal";
import "./AdminTracker.css"; // Ensure your dedicated style sheet is explicitly imported

export default function AdminTracker({ onBack, onNavigate }) {
  const [events, setEvents] = useState({});
  const [delayedEvents, setDelayedEvents] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [modalItems, setModalItems] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshAnim, setRefreshAnim] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());

  // ⚡ Removed unused calendarRef assignment to eliminate ESLint warning

  const getFgsForDate = (date) => {
    if (!date) return [];
    const key = date.toDateString();
    const normalList = (events[key] || []).map((x) => ({ ...x, delayed: null }));
    const delayedList = (delayedEvents[key] || []);
    return [...normalList, ...delayedList];
  };

  const handleFgClick = (fgNumber) => {
    setSelectedDate(null);
    if (onNavigate) {
      onNavigate("progress", { selectedFG: fgNumber });
    }
  };

  const parseDate = useCallback((str) => {
    if (!str) return new Date();
    const [day, month, year] = str.split("/");
    return new Date(`${year}-${month}-${day}`);
  }, []);

  const workingDaysBetween = useCallback((start, end) => {
    let d = new Date(start);
    let days = 0;
    while (d <= end) {
      if (d.getDay() !== 0) days++; 
      d.setDate(d.getDate() + 1);
    }
    return days;
  }, []);

  const loadEvents = useCallback(async () => {
    setIsRefreshing(true);
    setRefreshAnim(false);
    const untreated = {};
    const delayed = {};
    const today = new Date();

    try {
      const progressSnap = await get(ref(rtdb, "Progress"));

      if (progressSnap.exists()) {
        progressSnap.forEach((child) => {
          const obj = child.val();
          const fgNum = String(obj.fgNumber || child.key).trim();
          if (!obj.receivingDate) return;

          const receiveDateObj = parseDate(obj.receivingDate);
          const dKey = receiveDateObj.toDateString();
          const workDays = workingDaysBetween(receiveDateObj, today);

          const normalizedItem = {
            ...obj,
            fgNumber: fgNum,
          };

          const isReceived = obj.s1 === true;
          const isTreated = obj.s2 === true;
          if (isReceived && !isTreated) {
            untreated[dKey] = untreated[dKey] || [];
            untreated[dKey].push(normalizedItem);
          }

          const isDelivered = obj.s5 === true;
          if (!isDelivered && workDays > 6) {
            delayed[dKey] = delayed[dKey] || [];
            delayed[dKey].push({ ...normalizedItem, delayed: workDays });
          }
        });
      }

      setEvents(untreated);
      setDelayedEvents(delayed);
      setTimeout(() => setRefreshAnim(true), 100);
    } catch (err) {
      console.error("Error evaluating factory tracking profiles:", err);
    } finally {
      setIsRefreshing(false);
    }
  }, [parseDate, workingDaysBetween]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const dateClick = (date) => {
    const items = getFgsForDate(date);
    if (items.length === 0) return; 
    setModalItems(items);
    setSelectedDate(date);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)),
    onSwipedRight: () => setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  return (
    <div className="admin-tracker-page">
      <div className="admin-tracker-page__bg-blob admin-tracker-page__blob-top"></div>
      <div className="admin-tracker-page__bg-blob admin-tracker-page__blob-bottom"></div>
      
      {/* HEADER */}
      <div className="admin-tracker-page__header">
        <h2 className="admin-tracker-page__title">
          Tracker Calendar
        </h2>

        <button 
          onClick={loadEvents} 
          disabled={isRefreshing}
          className="admin-tracker-page__btn"
          style={{ 
            cursor: isRefreshing ? 'not-allowed' : 'pointer', 
            backgroundColor: isRefreshing ? '#94a3b8' : '#ffffff'
          }}
        >
          {isRefreshing && <span className="admin-tracker-page__spinner">🔄</span>}
          {isRefreshing ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* CALENDAR SECTION */}
      <div 
        {...swipeHandlers} 
        className={`admin-tracker-page__calendar-container ${refreshAnim ? "admin-tracker-page__fade-in" : ""}`} 
      >
        <div className="admin-tracker-page__calendar-inner">
          <Calendar
            onClickDay={dateClick}
            value={calendarDate}
            onActiveStartDateChange={({ activeStartDate }) => setCalendarDate(activeStartDate)}
            tileContent={({ date }) => {
              const key = date.toDateString();
              return (
                <div className="admin-tracker-page__tile-dots">
                  {events[key] && <div className="admin-tracker-page__dot admin-tracker-page__dot--wip"></div>}
                  {delayedEvents[key] && <div className="admin-tracker-page__dot admin-tracker-page__dot--delayed"></div>}
                </div>
              );
            }}
          />

          {/* 📊 VISUAL STATUS LEGEND ROW */}
          <div className="admin-tracker-page__legend-container">
            <div className="admin-tracker-page__legend-item">
              <span className="admin-tracker-page__legend-indicator admin-tracker-page__legend-indicator--wip"></span>
              <span className="admin-tracker-page__legend-text">Work In Progress (Untreated)</span>
            </div>
            <div className="admin-tracker-page__legend-item">
              <span className="admin-tracker-page__legend-indicator admin-tracker-page__legend-indicator--delayed"></span>
              <span className="admin-tracker-page__legend-text">Delayed Items (more than 6 Days)</span>
            </div>
          </div>

        </div>
      </div>

      {/* MODAL */}
      {selectedDate && (
        <FgDetailModal
          date={selectedDate}
          items={modalItems}
          onClose={() => setSelectedDate(null)}
          onFgClick={handleFgClick} 
        />
      )}
    </div>
  );
}